import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { subjectService } from '../../services/subjectService';
import { SUBJECT_TOPICS } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api'; // Added import for api

const emptyForm = { name: '' };

export default function SubjectsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ name: '', topicCount: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch subjects and topics
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch subjects
      const subjectsResponse = await subjectService.list();
      const subjects = subjectsResponse.data.results || subjectsResponse.data || [];
      setRows(subjects);
      
      // Fetch all topics to calculate counts
      const topicsResponse = await api.get(SUBJECT_TOPICS);
      const allTopics = topicsResponse.data.results || topicsResponse.data || [];
      
      // Calculate topic count for each subject
      const subjectsWithCounts = subjects.map(subject => {
        const topicCount = allTopics.filter(topic => topic.subject === subject.id).length;
        return {
          ...subject,
          topicCount
        };
      });
      
      setRows(subjectsWithCounts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = (row) => {
    if (row) {
      if (row.topicCount > 0) {
        showSnackbar(`Cannot edit '${row.name}': Subject has ${row.topicCount} associated topic${row.topicCount > 1 ? 's' : ''}`, 'warning');
        return;
      }
      setEditingId(row.id);
      setForm({ name: row.name });
    } else {
      setEditingId(null);
      setForm(emptyForm);
    }
    setDialogOpen(true);
  };

  const handleClose = () => setDialogOpen(false);

  const handleDelete = async (id) => {
    const subject = rows.find(row => row.id === id);
    if (subject && subject.topicCount > 0) {
      showSnackbar(`Cannot delete '${subject.name}': Subject has ${subject.topicCount} associated topic${subject.topicCount > 1 ? 's' : ''}`, 'warning');
      return;
    }
    
    if (!window.confirm('Delete this subject?')) return;
    try {
      await subjectService.remove(id);
      showSnackbar('Subject deleted successfully');
      fetchData();
    } catch (e) {
      showSnackbar('Delete failed', 'error');
    }
  };

  const handleSubmit = async () => {
    if (!form.name) {
      showSnackbar('Subject name is required', 'error');
      return;
    }
    try {
      if (editingId) {
        await subjectService.update(editingId, form);
        showSnackbar('Subject updated successfully');
      } else {
        await subjectService.create(form);
        showSnackbar('Subject created successfully');
      }

      handleClose();
      fetchData();
    } catch (e) {
      console.error(e);
      showSnackbar('Operation failed', 'error');
    }
  };

  const filteredRows = filterData(rows, filters, (row, filters) => {
    // Custom filter logic for topic count
    if (filters.topicCount && filters.topicCount !== '') {
      if (filters.topicCount === '0' && row.topicCount !== 0) {
        return false;
      }
      if (filters.topicCount === '1+' && row.topicCount === 0) {
        return false;
      }
    }
    return true;
  });

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { name: '', topicCount: '' });

  const filterConfig = [
    {
      field: 'name',
      label: 'Subject Name',
      type: 'text',
      placeholder: 'Search by subject name...'
    },
    {
      field: 'topicCount',
      label: 'Has Topics',
      type: 'select',
      options: [
        { value: '', label: 'All' },
        { value: '0', label: 'No Topics' },
        { value: '1+', label: 'Has Topics' }
      ]
    }
  ];

  const columns = [
    {
      field: 'name',
      headerName: 'Subject Name',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={params.value} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>
      )
    },
    {
      field: 'topicCount',
      headerName: (
        <Tooltip title="Number of topics associated with this subject. Subjects with topics cannot be edited or deleted.">
          <span>Topic Count</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value > 0 ? "secondary" : "default"}
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const hasTopics = params.row.topicCount > 0;
        const editTooltip = hasTopics 
          ? 'Cannot edit: Subject has associated topics' 
          : 'Edit subject';
        const deleteTooltip = hasTopics 
          ? 'Cannot delete: Subject has associated topics' 
          : 'Delete subject';
        
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={editTooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleOpen(params.row)}
                  disabled={hasTopics}
                  sx={{ p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={deleteTooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(params.row.id)}
                  disabled={hasTopics}
                  sx={{ p: 0.5, color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  const actionButtons = [
    {
      label: 'Add Subject',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No subjects found',
    description: 'Try adjusting your filters or add your first subject.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  // Calculate statistics for display
  const subjectsWithTopics = rows.filter(row => row.topicCount > 0).length;
  const subjectsWithoutTopics = rows.filter(row => row.topicCount === 0).length;

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Subjects"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredRows.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        {/* Statistics Display */}
        {!loading && rows.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Subject Statistics
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`${subjectsWithTopics} with topics`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip 
                label={`${subjectsWithoutTopics} without topics`}
                size="small"
                color="default"
                variant="outlined"
              />
              <Chip 
                label={`${rows.length} total subjects`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
        
        <DataGrid
          rows={filteredRows}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Subject' : 'Add Subject'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Subject Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            autoFocus
          />
          {editingId ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note: Editing a subject name will not affect existing topics. Topics will continue to reference this subject.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note: After creating a subject, you can add topics to it in the "Manage Curriculum" section.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
